import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { type ErrorType } from "@vega-api/configs/customClientQuery";
import { AnalyticsEvent } from "@vega-api/custom/analytics/analyticsEvents";
import { useAnalytics } from "@vega-api/custom/hooks/useAnalytics";
import { useSignNdaForDealOpportunity } from "@vega-api/generated/coinvest-deal-opportunity-actions/coinvest-deal-opportunity-actions";
import { getGetDealOpportunityLimitedPartnerViewV3QueryKey } from "@vega-api/generated/deal-opportunity-lp-view/deal-opportunity-lp-view";
import { NdaSignatureAction } from "@vega-api/generated/types/ndaSignatureAction";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { trackCoInvestNdaSignAttempted } from "@vega-core/utils/posthog/coInvestAnalytics";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";
import { useOrganization } from "@vega-modules/hooks/useOrganization";

type UseNdaSignatureProps = {
  dealOpportunityId: string;
  dealId?: string;
  documentId?: string;
};

export const useNdaSignature = ({
  dealOpportunityId,
  dealId,
  documentId,
}: UseNdaSignatureProps) => {
  const { trackEvent } = useAnalytics();
  const { selectedOrgId: limitedPartnerId } = useOrganization();
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();

  const { mutate: signNda, isPending: isSigningNda } =
    useSignNdaForDealOpportunity({
      mutation: {
        onSuccess: () => {
          // Track NDA signed
          trackEvent(AnalyticsEvent.COINVEST_NDA_SIGNED, undefined, {
            documentId,
            dealId,
            dealOpportunityId,
            limitedPartnerId,
          });

          // Show success toast
          showToast({
            variant: "success",
            title: m.status_success(),
            description: m.coInvestments_nda_signSuccess(),
          });

          // Invalidate and refetch deal opportunity data to get updated status
          queryClient.invalidateQueries({
            queryKey:
              getGetDealOpportunityLimitedPartnerViewV3QueryKey(
                dealOpportunityId,
              ),
          });
        },
        onError: (error: ErrorType<unknown>) => {
          showErrorToast(error, {
            defaultMessage: m.coInvestments_error_signNda(),
          });
        },
      },
    });

  const handleSignNda = useCallback(
    () =>
      new Promise<void>((resolve, reject) => {
        trackCoInvestNdaSignAttempted({ dealOpportunityId, dealId });
        signNda(
          {
            dealOpportunityId,
            data: {
              action: NdaSignatureAction.SIGN,
            },
          },
          {
            onSuccess: () => {
              resolve();
            },
            onError: (error) => {
              reject(error);
            },
          },
        );
      }),
    [dealOpportunityId, signNda],
  );

  return {
    handleSignNda,
    isSigningNda,
    isLoading: isSigningNda,
  };
};
